import type {ReactElement} from 'react';
import type {GridOptions, ListOptions, SkeletonLoaderProps, BarOptions, BarCSSProperties, GridCSSProperties, ListCSSProperties} from './skeleton-loader.types';
import {getResolvedOptions} from './skeleton-loader.types';
import {SkeletonLoaderVariant} from './skeleton-loader.types';
import styles from './skeleton-loader.module.scss';

export function SkeletonLoader({isAnimated = false, isPulsating = true, className = '', variant: loaderVariant, options: loaderOptions}: SkeletonLoaderProps): ReactElement {
  const formatSize = (size: string | number | undefined): string | undefined => {
    if (size === undefined) {
      return undefined;
    }

    return typeof size === 'number' ? `${size}px` : size;
  };

  const getPulseClass = (): string => {
    if (!isPulsating) {
      return styles.static;
    }
    return styles.pulse;
  };

  const getAnimatedClass = (): string => {
    return isAnimated ? styles.animated : '';
  };

  const renderBars = (barOpts: Required<BarOptions>): ReactElement => {
    const {barThickness, barWidths, barCount} = barOpts ?? {};

    const count = barCount ?? barWidths.length;
    const bars = Array.from({length: count}, (_, index) => {
      const width = barWidths[index % barWidths.length];
      return {width};
    });

    const barContainerStyle: BarCSSProperties = {
      '--bar-thickness': `${barThickness}px`,
    };

    return (
      <div className={`${styles.barContainer} ${getAnimatedClass()}`} style={barContainerStyle}>
        {bars.map((bar, index) => {
          const animationClass = isAnimated ? (isPulsating ? styles.barAnimatedPulsing : styles.barAnimated) : '';
          const barStyle: BarCSSProperties = {
            width: formatSize(bar.width),
            '--delay': `${index * 0.15}s`,
          };

          return <div key={index} className={`${styles.bar} ${getPulseClass()} ${animationClass}`} style={barStyle} />;
        })}
      </div>
    );
  };

  const renderGrid = (gridOpts: Required<GridOptions>): ReactElement => {
    const {itemCount, columns, gap, gridItemHeight, gridItemWidth} = gridOpts;
    const items = Array.from({length: itemCount}, (_, index) => index);

    const containerStyle: GridCSSProperties = {
      '--grid-columns': columns,
      '--grid-gap': `${gap}px`,
      '--grid-item-height': formatSize(gridItemHeight),
      '--grid-item-width': formatSize(gridItemWidth),
      '--grid-aspect-ratio': gridItemHeight !== 'auto' ? 'auto' : '1',
    };

    return (
      <div className={`${styles.gridContainer} ${getAnimatedClass()}`} style={containerStyle}>
        {items.map((index) => {
          const itemStyle: GridCSSProperties = {
            '--delay': `${index * 0.1}s`,
          };
          return <div key={index} className={`${styles.gridItem} ${getPulseClass()} ${isAnimated ? styles.gridItemAnimated : ''}`} style={itemStyle} />;
        })}
      </div>
    );
  };

  const renderList = (listOpts: Required<ListOptions>): ReactElement => {
    const {itemCount, gap} = listOpts;
    const items = Array.from({length: itemCount}, (_, index) => index);

    const containerStyle: ListCSSProperties = {
      '--list-gap': `${gap}px`,
    };

    return (
      <div className={`${styles.listContainer} ${getAnimatedClass()}`} style={containerStyle}>
        {items.map((index) => {
          const itemStyle: ListCSSProperties = {
            '--delay': `${index * 0.08}s`,
          };
          return (
            <div key={index} className={`${styles.listItem} ${isAnimated ? styles.listItemAnimated : ''}`} style={itemStyle}>
              <div className={`${styles.listItemAvatar} ${getPulseClass()}`} />
              <div className={styles.listItemContent}>
                <div className={`${styles.listItemTitle} ${getPulseClass()}`} />
                <div className={`${styles.listItemSubtitle} ${getPulseClass()}`} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCard = (): ReactElement => {
    return (
      <div className={`${styles.cardContainer} ${getAnimatedClass()} ${isAnimated ? styles.cardAnimated : ''}`}>
        <div className={`${styles.cardImage} ${getPulseClass()}`} />
        <div className={styles.cardBody}>
          <div className={`${styles.cardTitle} ${getPulseClass()}`} />
          <div className={`${styles.cardText} ${getPulseClass()}`} />
          <div className={`${styles.cardTextShort} ${getPulseClass()}`} />
        </div>
      </div>
    );
  };

  const renderTextBlock = (): ReactElement => {
    return (
      <div className={`${styles.textBlockContainer} ${getAnimatedClass()}`}>
        <div className={`${styles.textLine} ${styles.textLineFull} ${getPulseClass()} ${isAnimated ? styles.textLineAnimated : ''}`} />
        <div className={`${styles.textLine} ${styles.textLineFull} ${getPulseClass()} ${isAnimated ? styles.textLineAnimated : ''}`} />
        <div className={`${styles.textLine} ${styles.textLineMedium} ${getPulseClass()} ${isAnimated ? styles.textLineAnimated : ''}`} />
        <div className={`${styles.textLine} ${styles.textLineFull} ${getPulseClass()} ${isAnimated ? styles.textLineAnimated : ''}`} />
        <div className={`${styles.textLine} ${styles.textLineShort} ${getPulseClass()} ${isAnimated ? styles.textLineAnimated : ''}`} />
      </div>
    );
  };

  const renderVariant = (): ReactElement => {
    switch (loaderVariant) {
      case SkeletonLoaderVariant.GRID:
        return renderGrid(getResolvedOptions(loaderVariant, loaderOptions));
      case SkeletonLoaderVariant.BARS:
        return renderBars(getResolvedOptions(loaderVariant, loaderOptions));
      case SkeletonLoaderVariant.LIST:
        return renderList(getResolvedOptions(loaderVariant, loaderOptions));
      case SkeletonLoaderVariant.CARD:
        return renderCard();
      case SkeletonLoaderVariant.TEXT_BLOCK:
        return renderTextBlock();
      default:
        return renderTextBlock();
    }
  };

  return (
    <div className={`${styles.container} ${className}`} aria-hidden="true">
      {renderVariant()}
    </div>
  );
}
